import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Badge,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from 'reactstrap';
import DownloadIcon from 'mdi-react/DownloadIcon';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import AccountMultipleIcon from 'mdi-react/AccountMultipleIcon';
import CashIcon from 'mdi-react/CashIcon';
import CreditCardMultipleIcon from 'mdi-react/CreditCardMultipleIcon';
import CloseCircleOutlineIcon from 'mdi-react/CloseCircleOutlineIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import fileDownload from 'js-file-download';
import { Parser } from 'json2csv';
import CheckIcon from '../../shared/components/custom/CheckIcon';
import Loading from '../../shared/components/custom/Loading';
import DeleteModal from '../../shared/components/custom/DeleteModal';
import SquareIconButton from '../../shared/components/custom/SquareIconButton';
import useModal from '../../shared/hooks/useModal';
import DisplayCounter from '../_uiComponents/DisplayCounter';
import Api from '../../util/api';
import { formatCurrencyNumber } from '../../util/functions';
import { partial } from '../../util/translation/translation';
import useConfig from '../../util/useConfig';
import { formatDateTime } from '../../util/date-and-time';
import exportToExcelFile from '../../util/xlsx';
import AddCompanySubscriptionModal from './components/AddCompanySubscriptionModal';

// Same subscriber can show up more than once (e.g. they cancelled and
// resubscribed), which read as duplicate rows. Group them by subscriber so
// each person/company is one row with their current subscription, and any
// older ones tucked under an expand toggle instead of repeated inline.
const getSubscriberKey = (row) => {
  if (row.companyId) return `company-${row.companyId}`;
  if (row.userId) return `user-${row.userId}`;
  if (row.userEmail) return `guest-email-${row.userEmail}`;
  if (row.userName) return `guest-name-${row.userName}`;
  return `subscription-${row.id}`;
};

const SCORE_TYPE_LABELS = {
  goal: 'scoreTypeGoal',
  point: 'scoreTypePoint',
  attendant: 'scoreTypeAttendant',
  holeInOne: 'scoreTypeHoleInOne',
  medal: 'scoreTypeMedal',
};

// Running = charged for scores, or about to be once a card checkout finishes
const isRunning = (row) => row.status === 'ACTIVE' || row.status === 'PENDING';

const groupSubscriptions = (subscriptions) => {
  const order = [];
  const groups = new Map();
  subscriptions.forEach((row) => {
    const key = getSubscriberKey(row);
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key).push(row);
  });
  return order.map((key) => {
    const items = groups.get(key);
    const primary = items.find((item) => item.status === 'ACTIVE') || items[0];
    const history = items.filter((item) => item !== primary);
    // More than one running subscription: each is charged for every score
    const runningCount = items.filter(isRunning).length;
    return { key, primary, history, runningCount };
  });
};

const SubscriptionListPage = ({ planId, scoreType }) => {
  const config = useConfig();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const toggleExpanded = (key) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  const groupedSubscriptions = useMemo(
    () => groupSubscriptions(subscriptions),
    [subscriptions]
  );
  const stats = useMemo(() => {
    const active = subscriptions.filter((item) => item.status === 'ACTIVE');
    const pending = subscriptions.filter((item) => item.status === 'PENDING');
    const sumPricePerGoal = active.reduce(
      (sum, item) => sum + Number(item.amountPerGoal || 0),
      0
    );
    const invoiceCount = active.filter((item) => item.isExternalInvoice).length;
    const cardCount = active.length - invoiceCount;
    const cardPercentage = active.length
      ? Math.round((cardCount / active.length) * 100)
      : 0;
    const invoicePercentage = active.length ? 100 - cardPercentage : 0;
    const activeAndPendingCount = active.length + pending.length;
    const averagePricePerGoal = activeAndPendingCount
      ? sumPricePerGoal / activeAndPendingCount
      : 0;
    return {
      activeCount: active.length,
      sumPricePerGoal,
      averagePricePerGoal,
      cardPercentage,
      invoicePercentage,
    };
  }, [subscriptions]);

  const p = partial('goalSponsor');
  const s = partial('shared');
  const po = partial('paymentOptions');
  const scoreLabel = p(SCORE_TYPE_LABELS[scoreType] || SCORE_TYPE_LABELS.goal);
  const [loadingExport, setLoadingExport] = useState(false);
  const [handleCancelModal, cancelModalVisible, cancelId] = useModal();
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  // Companies already supporting the plan aren't offered again
  const activeCompanyIds = useMemo(
    () =>
      subscriptions
        .filter((item) => item.status === 'ACTIVE' && item.companyId)
        .map((item) => item.companyId),
    [subscriptions]
  );

  // Cancels at Stripe too and emails the supporter; the row stays as history
  // Pending: a card checkout that was never completed. Removing it frees the
  // subscriber (or company) to sign up again; nothing was charged.
  const cancellingPending =
    subscriptions.find((item) => item.id === cancelId)?.status === 'PENDING';

  const cancelSubscription = async () => {
    setLoadingSubscriptions(true);
    try {
      await Api.goalSponsor.subscriptions.cancelSubscription(cancelId);
      toast.success(
        cancellingPending ? p('signupRemoved') : p('subscriptionCancelled')
      );
    } catch (err) {
      toast.error(p('subscriptionCancelERROR'));
    } finally {
      handleCancelModal();
      fetchSubscriptions();
    }
  };

  const renderCancelCell = (subscription) => (
    <td>
      {isRunning(subscription) && (
        <div className="icon-actions">
          <SquareIconButton
            icon={<CloseCircleOutlineIcon />}
            onClick={() => handleCancelModal(subscription.id)}
            title={
              subscription.status === 'PENDING'
                ? p('removeSignup')
                : p('cancelSubscription')
            }
            tone="danger"
            id={`cancel-subscription-${subscription.id}`}
          />
        </div>
      )}
    </td>
  );

  const fetchSubscriptions = async () => {
    setLoadingSubscriptions(true);
    try {
      const resSubscriptions =
        await Api.goalSponsor.subscriptions.getSubscriptionsForPlan(planId);

      const companyIds = resSubscriptions
        .filter((row) => row.companyId)
        .map((row) => row.companyId);
      const userCloudIds = resSubscriptions
        .filter((row) => row.userId)
        .map((row) => row.userId);
      const [{ data: foundUsers }, companies, resSubscriptionsPaymentOptions] =
        await Promise.all([
          Api.users.getUsersByCloudIds(userCloudIds),
          companyIds.length > 0
            ? Api.companies.getCompaniesByIds(companyIds)
            : Promise.resolve([]),
          Api.transactions.getPaymentOptions(planId),
        ]);
      const mappedSubscriptions = resSubscriptions
        .map((row) => {
          const foundCompany = companies.find(
            (company) => company.id === row.companyId
          );
          const customCompanyName = foundCompany
            ? foundCompany.clubRelations.find(
                (relation) => relation.clubId == config.ntb_team_id
              )?.name
            : null;
          const foundUser = foundUsers.find(
            (user) => user.userCloudId === row.userId
          );
          return {
            ...row,
            company: foundCompany
              ? { ...foundCompany, customCompanyName }
              : null,
            user: foundUser,
            paymentOption: resSubscriptionsPaymentOptions.find(
              (paymentOption) => paymentOption.id === row.paymentOptionId
            ),
          };
        })
        .sort((a, b) => b.paymentOption.price - a.paymentOption.price);

      setSubscriptions(mappedSubscriptions);
      setLoadingSubscriptions(false);
    } catch (err) {
      toast.error(err || `${p('auctionRetrievingERROR')}`);
      setLoadingSubscriptions(false);
    }
  };
  useEffect(() => {
    fetchSubscriptions();
  }, []);

  // Shared by both export formats - rows keyed by their display label, so
  // CSV/XLSX headers come straight from the object keys with no separate
  // label/value mapping to keep in sync.
  const buildExportRows = async () => {
    const exportData =
      await Api.goalSponsor.subscriptions.exportSubscriptions(planId);

    // Resolve company names for rows with companyId — matching the table display
    const companyIds = exportData
      .filter((row) => row.companyId)
      .map((row) => row.companyId);
    const companies =
      companyIds.length > 0
        ? await Api.companies.getCompaniesByIds(companyIds)
        : [];

    return exportData.map((row) => {
      const foundCompany = companies.find((c) => c.id === row.companyId);
      const companyName = foundCompany
        ? foundCompany.clubRelations.find(
            (relation) => relation.clubId == config.ntb_team_id
          )?.name
        : null;

      return {
        [s('name')]: companyName || row.userName || '',
        [s('email')]: row.userEmail || '',
        [s('paymentMethod')]: row.isExternalInvoice
          ? s('invoice')
          : po('providerType-stripe'),
        [p('totalScoresCharged', { score: scoreLabel })]:
          row.totalGoalsCharged ?? 0,
        [p('totalAmountCharged')]:
          `${formatCurrencyNumber(Number(row.totalAmountChargedOre) / 100)} ${config.currencyCaps}`,
        [s('status')]: row.status || '',
        [p('subscriptionPlan')]: row.planName || '',
        [s('date')]: row.createdAt ? formatDateTime(row.createdAt) : '',
      };
    });
  };

  const handleExportCsv = async () => {
    try {
      setLoadingExport(true);
      const rows = await buildExportRows();
      const csv = new Parser().parse(rows);
      fileDownload(`﻿${csv}`, `subscriptions-${planId}.csv`);
    } catch {
      toast.error(p('subscriptionPlansRetrievingERROR'));
    } finally {
      setLoadingExport(false);
    }
  };

  const handleExportXlsx = async () => {
    try {
      setLoadingExport(true);
      const rows = await buildExportRows();
      exportToExcelFile(rows, `subscriptions-${planId}`);
    } catch {
      toast.error(p('subscriptionPlansRetrievingERROR'));
    } finally {
      setLoadingExport(false);
    }
  };

  const historyBadge = (historyCount) =>
    historyCount > 0 && (
      <Badge pill color="secondary" className="marginLeft-8">
        +{historyCount} {p('previousSubscriptions')}
      </Badge>
    );

  const renderCompanyCell = (subscription) =>
    subscription.company ? (
      <Link to={`/companies/edit/${subscription.company.id}`}>
        {subscription.company?.customCompanyName}
      </Link>
    ) : (
      '-'
    );

  const renderUserCell = (subscription, historyCount = 0) => {
    if (subscription.user) {
      return (
        <div className="flex-Vcenter">
          <Link to={`/users/edit/${subscription.user.id}`}>
            {subscription.user?.firstname} {subscription.user?.lastname}
          </Link>
          {historyBadge(historyCount)}
        </div>
      );
    }
    if (subscription.userName || subscription.userEmail) {
      return (
        <div className="flex-Vcenter">
          <span>{subscription.userName || subscription.userEmail}</span>
          <Badge pill color="secondary" className="marginLeft-8">
            {s('website')}
          </Badge>
          {historyBadge(historyCount)}
        </div>
      );
    }
    return historyCount > 0 ? (
      <div className="flex-Vcenter">
        <span>-</span>
        {historyBadge(historyCount)}
      </div>
    ) : (
      '-'
    );
  };

  const renderValueCells = (subscription) => (
    <>
      <td>
        <b>
          {formatCurrencyNumber(Number(subscription.amountPerGoal) / 100)}{' '}
          {config.currencyCaps}
        </b>
      </td>
      <td>
        {formatCurrencyNumber(Number(subscription.capPerGame) / 100)}{' '}
        {config.currencyCaps}
      </td>
      <td>
        <Badge color={subscription.isExternalInvoice ? 'warning' : 'info'}>
          {subscription.isExternalInvoice
            ? s('invoice')
            : po('providerType-stripe')}
        </Badge>
      </td>
    </>
  );

  const duplicateBadge = (key, runningCount) =>
    runningCount > 1 && (
      <>
        <Badge
          pill
          color="danger"
          className="marginLeft-8"
          id={`duplicate-${key}`}
        >
          {p('duplicate')}
        </Badge>
        <UncontrolledTooltip target={`duplicate-${key}`}>
          {p('duplicateInfo', { count: runningCount })}
        </UncontrolledTooltip>
      </>
    );

  const renderSubscriptions = () => {
    return groupedSubscriptions.flatMap(
      ({ key, primary, history, runningCount }, i) => {
        const isExpanded = expandedKeys.has(key);
        const rows = [
          <tr
            style={{ background: i % 2 ? '#fff' : '#F9FAFB' }}
            key={`subscription-${primary.id}`}
          >
            <td>
              {history.length > 0 ? (
                <SquareIconButton
                  icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                  onClick={() => toggleExpanded(key)}
                  title={isExpanded ? s('collapse') : s('expand')}
                  id={`toggle-expand-${primary.id}`}
                />
              ) : null}
            </td>
            <td>
              <CheckIcon state={primary.status === 'ACTIVE'} />
            </td>
            <td>
              <div className="flex-Vcenter">
                {renderCompanyCell(primary)}
                {/* Keys can hold characters ids can't (e.g. emails) */}
                {duplicateBadge(String(i), runningCount)}
              </div>
            </td>
            <td>{renderUserCell(primary, history.length)}</td>
            {renderValueCells(primary)}
            {renderCancelCell(primary)}
          </tr>,
        ];
        if (isExpanded) {
          history.forEach((subscription) => {
            rows.push(
              <tr
                style={{ background: '#F4F5F7' }}
                key={`subscription-${subscription.id}`}
              >
                <td />
                <td>
                  <CheckIcon state={subscription.status === 'ACTIVE'} />
                </td>
                <td>{renderCompanyCell(subscription)}</td>
                <td>
                  {isRunning(subscription) ? (
                    <Badge pill color="danger">
                      {p('alsoRunning')}
                    </Badge>
                  ) : (
                    <small className="text-muted">
                      {p('previousSubscription')}
                    </small>
                  )}
                </td>
                {renderValueCells(subscription)}
                {renderCancelCell(subscription)}
              </tr>
            );
          });
        }
        return rows;
      }
    );
  };

  return (
    <Container>
      <Loading loading={loadingSubscriptions || loadingExport} />
      <DeleteModal
        visible={cancelModalVisible}
        title={
          cancellingPending
            ? p('removeSignupConfirm')
            : p('cancelSubscriptionConfirm')
        }
        actionLabel={
          cancellingPending ? p('removeSignup') : p('cancelSubscription')
        }
        handleModal={handleCancelModal}
        modalAction={cancelSubscription}
      />
      <AddCompanySubscriptionModal
        isOpen={addCompanyOpen}
        onClose={() => setAddCompanyOpen(false)}
        onAdded={fetchSubscriptions}
        planId={planId}
        scoreLabel={scoreLabel}
        activeCompanyIds={activeCompanyIds}
      />
      <Row className="mb-3">
        <Col className="flex justify-content-end" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn btn-primary btn-sm icon mb-2"
            onClick={() => setAddCompanyOpen(true)}
          >
            <PlusIcon /> {p('addCompany')}
          </button>
          <UncontrolledDropdown>
            <DropdownToggle className="icon icon--right mb-2" outline size="sm">
              <DownloadIcon /> {s('export')} <ChevronDownIcon />
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem onClick={handleExportXlsx}>
                {s('export')} (XLSX)
              </DropdownItem>
              <DropdownItem onClick={handleExportCsv}>
                {s('export')} (CSV)
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </Col>
      </Row>
      <Row>
        <Col>
          <DisplayCounter
            id="subscriptions-active-count"
            title={p('activeSubscriptions')}
            value={`${stats.activeCount}`}
            icon={<AccountMultipleIcon style={{ width: 18 }} />}
          />
          <DisplayCounter
            id="subscriptions-sum-price-per-goal"
            title={p('pricePerScoreLabel', { score: scoreLabel })}
            value={`${formatCurrencyNumber(stats.sumPricePerGoal / 100)} ${config.currencyCaps}`}
            text={`${s('active')} · ${s('average')} ${formatCurrencyNumber(stats.averagePricePerGoal / 100)} ${config.currencyCaps}`}
            icon={<CashIcon style={{ width: 18 }} />}
          />
          <DisplayCounter
            id="subscriptions-payment-method-split"
            title={po('providerType-stripe')}
            value={`${stats.cardPercentage}%`}
            text={`${stats.invoicePercentage}% ${s('invoice')}`}
            icon={<CreditCardMultipleIcon style={{ width: 18 }} />}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <div
            style={{
              display: 'flex',
              paddingBottom: 5,
              background: '#fff',
              borderRadius: 6,
              boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.08)',
            }}
          >
            <Table responsive striped>
              <thead>
                <tr>
                  <th />
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">{s('active')}</div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">{s('company')}</div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">{s('user')}</div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">
                      {p('pricePerScoreLabel', { score: scoreLabel })}
                    </div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">
                      {p('goalCapPerGame')}
                    </div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">
                      {s('paymentMethod')}
                    </div>
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>{renderSubscriptions()}</tbody>
            </Table>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default SubscriptionListPage;
